import { Button, Grid, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { missingField } from 'app/constants';
import { IExperience } from 'app/models/Placement/PlacementDetails';
import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { usePromiseTracker } from 'react-promise-tracker';
import { useSelector, useDispatch } from 'react-redux';
import { Concatenate } from 'utils/string/string';
import {
  selectSubmissionInfo,
  selectServiceFailures,
} from '../../../../../../../../../store/redux-store/placement-summary/selectors';
import { RequirementsRow } from '../../TableComponents/TableComponents';
import { ModalTableHeader } from 'app/models/Orders/OrderDetails';
import { AddOnContext } from '../../../PlacementSummaryTab';
import { placementSummaryActions } from '../../../../../../../../../store/redux-store/placement-summary/slice';
import { selectPlacementOrderType } from 'store/redux-store/placement-details/selectors';
import { getRequirementCheck } from '../../../helper';
import { TableSection } from '../../../customComponents/TableSection';
import { useRequirementFilter } from '../../../Helpers/requirementFilter';
import { ListContent } from 'app/components/Common/List/ListContent';

const SkillsHeader: ModalTableHeader[] = [
  { title: 'skillset', value: 'Skill Set' },
  { title: 'lastThreeYearsYearsOfExperience', value: 'Last 3 Years' },
  { title: 'totalYearsOfExperience', value: 'Total Experience' },
];

function getExperienceData(years: number | undefined, months: number | undefined) {
  if ((years == null && months == null) || (years === 0 && months === 0)) return '';
  else if (years == null || years === 0) return `${months} mo`;
  else if (months == null || months === 0) return `${years} yr`;
  else return `${years} yr, ${months} mo`;
}

function getExperienceDataWithFormating(years: number | undefined, months: number | undefined) {
  if ((years == null && months == null) || (years === 0 && months === 0)) return missingField;
  else if (years == null || years === 0) return `${months} mo`;
  else if (months == null || months === 0) return `${years} yr`;
  else return `${years} yr, ${months} mo`;
}

const useStyles = makeStyles()({
  yoeCell: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    gap: 6,
  },
  button: {
    cursor: 'pointer',
    color: '#006FB9',
    justifyContent: 'flex-start',
    marginLeft: '3px',
    fontWeight: 450,
    textTransform: 'inherit',
    padding: 0,
    '&:hover': {
      background: 'none',
      textDecoration: 'underline',
    },
  },
  experienceModalText: {
    color: '#003E68',
    fontWeight: 'bold',
  },
});

export const Experience = () => {
  const { t } = useTranslation();
  const {
    amnReq: {
      amn: { experience: strikeExperince },
      candidate: { experience },
    },
    requirementFilter,
  } = useSelector(selectSubmissionInfo);
  const { experienceRequirement: serviceFailed } = useSelector(selectServiceFailures);
  const placementOrderType = useSelector(selectPlacementOrderType);

  const { placementId } = useContext(AddOnContext);

  const { promiseInProgress } = usePromiseTracker({
    area: 'placement-submission-experience-requirement',
    delay: 0,
  });

  const dispatch = useDispatch();
  const { display } = useRequirementFilter('submission-amn', requirementFilter, strikeExperince?.type);

  return display ? (
    <RequirementsRow
      id="placement-submission-amn-experience"
      title={t('placement.profile.summary.submission.amn.experince.title')}
      requirementCell={<StrikeExperience experience={strikeExperince} t={t} />}
      candidateCell={<RowValue experience={experience} t={t} />}
      loading={promiseInProgress}
      hasError={serviceFailed}
      onTryAgain={() => {
        dispatch(
          placementSummaryActions.getExperienceRequirement({
            placementId,
            placementOrderType,
            isRetryingService: true,
          }),
        );
      }}
    />
  ) : null;
};

const RowValue = ({ experience, t }) => {
  const { classes } = useStyles();
  const { setModalProps } = useContext(AddOnContext);
  const {
    candidateYearsOfExpBySkillsetInfo: experiences = [],
    totalYearsOfExp: yoe = 0,
    totalMonthsOfExp: moe,
  } = experience?.data || {};

  const flattenSkillsetData = (skillsetInfo: IExperience[]) =>
    skillsetInfo.reduce(
      (resultArray, info) => [
        ...resultArray,
        {
          skillset: Concatenate([info.disciplineAbbr, info.specialtyAbbr], ', '),
          lastThreeYearsYearsOfExperience: (
            <>
              {getExperienceDataWithFormating(
                info.lastThreeYearsYearsOfExperience,
                info.lastThreeYearsMonthsOfExperience,
              )}
            </>
          ),
          totalYearsOfExperience: (
            <>{getExperienceDataWithFormating(info.totalYearsOfExperience, info.totalMonthsOfExperience)}</>
          ),
        },
      ],
      [] as any[],
    );

  return (
    <Typography variant="body1" className={classes.yoeCell}>
      <div style={{ paddingTop: 1 }}>
        <b>{`${t('placement.profile.summary.submission.amn.experince.yearsOfExperience')}:`}</b>
      </div>
      {/* <div>{t('Years Of Experience:')}</div> */}
      <Button
        type="button"
        variant="text"
        disableRipple
        disableTouchRipple
        disableFocusRipple
        onClick={() =>
          setModalProps?.({
            onChange: e => setModalProps?.(null),
            title: `${t('placement.profile.summary.submission.amn.experince.title')}`,
            subTitle: (
              <span style={{ fontSize: '14px' }}>
                {`${t('placement.profile.summary.submission.amn.experince.yearsOfExperience')}: `}{' '}
                <b>{getExperienceData(yoe, moe)}</b>
              </span>
            ),
            columns: SkillsHeader,
            rows: flattenSkillsetData(experiences || []),
          })
        }
        disabled={!(yoe || moe)}
        classes={{ root: classes.button }}
      >
        {Number.isFinite(yoe) ? getExperienceData(yoe, moe) : missingField}
      </Button>
    </Typography>
  );
};

const StrikeExperience = ({ experience, t }) => (
  <TableSection
    items={[
      {
        icon: experience ? getRequirementCheck(experience.type) : null,
        primaryContent: (
          <Grid container>
            <Grid item xs={12}>
              <Typography>{t(experience?.message?.secondary)}</Typography>
            </Grid>
            <Grid item xs={12}>
              {experience?.message?.ternary && <ListContent list={experience?.message?.ternary} />}
            </Grid>
          </Grid>
        ),
      },
    ]}
  />
);
